import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Sparkles } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Cadastro() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirm_password: "",
    institution: "",
    subject: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm_password) {
      toast.error("As senhas não coincidem.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/auth/register", form);
      login(res.data.token, res.data.user);
      toast.success("Conta criada! Enviamos um e-mail para você confirmar seu endereço. 📧");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao criar conta.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-brand-bg">
      <div className="hidden lg:flex relative bg-gradient-to-br from-brand-blue to-brand-purple p-12 flex-col justify-between overflow-hidden">
        <div className="absolute inset-0 grid-dots opacity-10" />
        <Logo light />
        <div className="relative">
          <Sparkles className="w-10 h-10 text-white/90" />
          <h2 className="mt-5 font-heading text-3xl font-black text-white leading-tight">
            Crie quizzes incríveis <br /> e jogue ao vivo com a turma
          </h2>
          <p className="mt-4 text-white/80 max-w-md">
            Junte-se a professores que transformam suas aulas em jogos envolventes. Crie sua conta
            gratuitamente, sem cartão de crédito.
          </p>
        </div>
        <p className="relative text-white/60 text-sm">Transforme conhecimento em diversão!</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <Logo />
          </div>
          <h1 className="font-heading text-3xl font-black text-brand-ink">Criar conta</h1>
          <p className="mt-2 text-brand-muted">A inscrição é gratuita.</p>

          <Button
            onClick={googleLogin}
            variant="outline"
            className="mt-6 w-full rounded-xl h-12 border-2 gap-2"
            data-testid="google-signup-btn"
          >
            <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" />
            Continuar com Google
          </Button>

          <div className="my-6 flex items-center gap-3 text-xs text-brand-muted">
            <span className="flex-1 h-px bg-slate-200" /> ou <span className="flex-1 h-px bg-slate-200" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="name">Nome completo *</Label>
              <Input id="name" required value={form.name} onChange={set("name")} className="mt-1.5 rounded-xl h-11" data-testid="input-name" />
            </div>
            <div>
              <Label htmlFor="email">E-mail *</Label>
              <Input id="email" type="email" required value={form.email} onChange={set("email")} className="mt-1.5 rounded-xl h-11" data-testid="input-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="password">Senha *</Label>
                <Input id="password" type="password" required value={form.password} onChange={set("password")} className="mt-1.5 rounded-xl h-11" data-testid="input-password" />
              </div>
              <div>
                <Label htmlFor="confirm">Confirmar *</Label>
                <Input id="confirm" type="password" required value={form.confirm_password} onChange={set("confirm_password")} className="mt-1.5 rounded-xl h-11" data-testid="input-confirm" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="inst">Instituição</Label>
                <Input id="inst" value={form.institution} onChange={set("institution")} className="mt-1.5 rounded-xl h-11" data-testid="input-institution" />
              </div>
              <div>
                <Label htmlFor="subj">Disciplina</Label>
                <Input id="subj" value={form.subject} onChange={set("subject")} className="mt-1.5 rounded-xl h-11" data-testid="input-subject" />
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-full h-12 bg-brand-blue hover:bg-blue-700 font-semibold" data-testid="submit-cadastro">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Criar minha conta grátis"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-brand-muted">
            Já tem conta?{" "}
            <Link to="/login" className="text-brand-blue font-semibold hover:underline">
              Entrar
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
